import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, TrendingUp, Star } from 'lucide-react';
import PageGlow from "@/components/PageGlow";
import HeroWave from "@/components/HeroWave";
import { Picture } from "@/components/media/Picture";
import { caseCover, caseAvatar } from "@/lib/media/case-images";
import bowlCover from "@/assets/case-bowlscoreboard-cover.png.asset.json";
import bowlAvatar from "@/assets/case-bowlscoreboard-avatar.jpg.asset.json";

const GRAD = '#5048E5';
const fg      = 'hsl(var(--foreground))';
const fgMuted = 'hsl(var(--muted-foreground))';
const surface = 'hsl(var(--card))';
const border  = 'hsl(var(--border))';

const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

interface Stat { label: string; value: string }
interface Project {
  slug: string;
  name: string;
  industry: string;
  url: string;
  image?: string;
  /** Use the responsive <Picture> variants (Eclipse cover only). */
  responsiveCover?: boolean;
  title?: string;
  description: string;
  disclosure?: string;
  testimonial?: { quote: string; name: string; role: string; avatar?: string };
  services: string[];
  results?: string;
  stats?: Stat[];
}

const PROJECTS: Project[] = [
  {
    slug: 'eclipse-paving',
    name: 'Eclipse Paving',
    industry: 'Driveways & Paving',
    url: 'https://www.eclipsepaving.com',
    image: '/__l5e/assets-v1/c7ad0567-bb14-48c8-8e03-ad6c3e0763f7/case-eclipse-cover.webp',
    responsiveCover: true,
    description:
      'Justin runs Eclipse Paving, a specialist driveway and paving company. When he came to us he needed a professional online presence that would generate consistent, high-quality leads. We built his website from the ground up, handled full SEO and AEO optimisation, implemented Google Tag Manager and Analytics tracking, set up his Google Business Profile, and launched and continue to manage his Google Ads campaigns.',
    services: ['AI Web & App Design', 'SEO & AEO Optimisation', 'Google Ads', 'Google Tag Manager', 'Analytics Tracking', 'Google Business Setup'],
    results:
      'Eclipse Paving has received over £70,000 worth of work directly attributed to our ad campaigns. Justin is an ongoing client — we continue to manage his ads, monitor performance, and support his growth month on month.',
    testimonial: {
      quote:
        "From zero online presence to a fully managed digital operation. Morgan and the team explained everything in plain English, built us a site we're proud of, and the leads have been coming in ever since.",
      name: 'Justin Kirby',
      role: 'Founder, Eclipse Paving',
      avatar: caseAvatar.webpSrcSet,
    },
  },
  {
    slug: 'bowlscoreboard',
    name: 'BowlScoreboard — from client to partnership',
    industry: 'BowlScoreboard',
    url: 'https://bowlscoreboard.co.uk',
    image: bowlCover.url,
    description:
      "When BowlScoreboard came to us, the product was already the best on the market — bespoke, hand-built mechanical scoreboards winning clubs across the UK on quality alone. What it lacked was the brand, website and structure to support its growth.\n\nThrough our Vision to Launch package and a new website, we refreshed the brand and helped reshape the business model itself. Instead of clubs buying their own board, businesses can now sponsor the advertising space, allowing clubs to receive a scoreboard free of charge while sponsors gain a lasting presence in front of players, members and spectators.\n\nAnd because that sponsorship puts the scoreboard there, they're not just advertising at the club — they're the business that gave the club its scoreboard.\n\nDigitally Baffled now manages the sale of BowlScoreboard's advertising space, and works alongside its leadership on commercial strategy.\n\nWe believed in the business enough to become part of it.",
    disclosure:
      "Digitally Baffled runs BowlScoreboard's advertising sales and works alongside its leadership on commercial strategy — a partnership that grew out of this project.",
    testimonial: {
      quote:
        "We were an operational business making good sales when we sat down with Digitally Baffled. They rebuilt our brand and website — but the bigger thing was the idea they brought to the table: give clubs the board free, and fund it through local businesses sponsoring the advertising space. It changed how the whole company works. We ended up bringing them in to run our ad sales, and onto our commercial team. That says it all really.",
      name: 'Chris P Spry',
      role: 'Founder & CEO, BowlScoreboard',
      avatar: bowlAvatar.url,
    },
    services: ['Vision to Launch', 'Website'],
    results:
      "The restructured model turned a one-off product sale into a repeatable, sponsor-funded system — and turned us from BowlScoreboard's web and brand agency into the partner running its advertising sales and shaping its commercial strategy. It's a live, growing relationship.",
  },
];


// JSON-LD lives in ./case-studies.seo so route head() stays lightweight.

export default function PortfolioPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden py-10 md:py-14" style={{ background: 'hsl(var(--background))' }}>
        <HeroWave />
        <PageGlow tall />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.h1 variants={fadeUp} className="mb-5 text-4xl font-bold md:text-5xl lg:text-6xl"
              style={{ fontFamily: 'Outfit, sans-serif', color: fg, lineHeight: 1.06 }}>
              Real businesses,<br />
              <span style={{
                background: 'linear-gradient(135deg, #5048E5 0%, hsl(14 100% 57%) 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
              }}>real results</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mx-auto max-w-xl text-base leading-relaxed"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
              We do not do vanity metrics. Every project we take on has a clear goal — more customers, more revenue, less hassle. Here is what that looks like in practice.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Projects */}
      <section className="pb-24" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-6xl px-6">
          <div className="flex flex-col gap-16">
            {PROJECTS.map((project, i) => (
              <motion.article
                key={project.name}
                id={project.slug}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className="overflow-hidden rounded-3xl"
                style={{ background: surface, border: `1px solid ${border}` }}
              >
                {/* Image */}
                {project.image && (
                <div className="relative h-56 overflow-hidden md:h-72">
                  {project.responsiveCover ? (
                    <Picture
                      {...caseCover}
                      sizes="(max-width: 767px) 100vw, 50vw"
                      alt={`${project.name} project`}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <img
                      src={project.image}
                      alt={`${project.name} project`}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0" style={{
                    background: 'linear-gradient(to bottom, transparent 40%, hsla(228,45%,9%,0.85) 100%)',
                  }} />
                  {/* Industry badge */}
                  <div className="absolute left-5 top-5">
                    <span className="rounded-full px-3 py-1 text-xs font-semibold"
                      style={{ background: 'rgba(80,72,229,0.9)', color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {project.industry}
                    </span>
                  </div>
                  {/* Live link */}
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute right-5 top-5 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-opacity hover:opacity-80"
                    style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.25)', color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    Visit site <ArrowUpRight size={12} />
                  </a>
                </div>
                )}

                {/* Content */}
                <div className="p-7 md:p-10">
                  {!project.image && (
                    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                      <span className="rounded-full px-3 py-1 text-xs font-semibold"
                        style={{ background: 'rgba(80,72,229,0.9)', color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        {project.industry}
                      </span>
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-opacity hover:opacity-80"
                        style={{ background: 'hsla(243,75%,59%,0.1)', border: '1px solid hsla(243,75%,59%,0.25)', color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                      >
                        Visit site <ArrowUpRight size={12} />
                      </a>
                    </div>
                  )}
                  <div className={`flex flex-col gap-8 md:flex-row ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>

                    {/* Left: description */}
                    <div className="flex-1">
                      <h2 className="mb-3 text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
                        {project.name}
                      </h2>
                      {project.description.split('\n\n').map((para, pi) => (
                        <p key={pi} className="mb-4 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                          {para}
                        </p>
                      ))}

                      {/* Disclosure */}
                      {project.disclosure && (
                        <p className="mb-5 text-xs leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted, opacity: 0.75 }}>
                          {project.disclosure}
                        </p>
                      )}

                      {/* Services */}
                      <div className="mb-5 flex flex-wrap gap-2">
                        {project.services.map(s => (
                          <span key={s} className="rounded-full px-3 py-1 text-xs font-medium"
                            style={{ background: 'hsla(243,75%,59%,0.1)', border: '1px solid hsla(243,75%,59%,0.25)', color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                            {s}
                          </span>
                        ))}
                      </div>

                      {/* Results quote */}
                      {project.results && (
                      <div className="rounded-2xl p-4" style={{ background: 'hsla(243,75%,59%,0.07)', border: '1px solid hsla(243,75%,59%,0.15)' }}>
                        <div className="mb-2 flex items-center gap-2">
                          <TrendingUp size={14} style={{ color: GRAD }} />
                          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Results</span>
                        </div>
                        <p className="text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                          {project.results}
                        </p>
                      </div>
                      )}

                      {/* Client testimonial */}
                      {project.testimonial && (
                        <div className="mt-4 rounded-2xl p-4" style={{ background: 'hsla(243,75%,59%,0.07)', border: '1px solid hsla(243,75%,59%,0.15)' }}>
                          <div className="mb-2 flex items-center gap-1" aria-hidden="true">
                            {[...Array(5)].map((_, si) => (
                              <Star key={si} size={13} fill="hsl(45 100% 50%)" style={{ color: 'hsl(45 100% 50%)' }} />
                            ))}
                          </div>
                          <p className="mb-3 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                            "{project.testimonial.quote}"
                          </p>
                          <div className="flex items-center gap-3">
                            {project.testimonial.avatar && (
                              <img
                                src={project.testimonial.avatar}
                                alt={project.testimonial.name}
                                width={46}
                                height={46}
                                loading="lazy"
                                className="h-[46px] w-[46px] shrink-0 rounded-full object-cover"
                                style={{ border: `2px solid ${GRAD}`, objectPosition: project.testimonial.name === 'Chris P Spry' ? 'center 25%' : undefined }}
                              />
                            )}
                            <div>
                              <p className="text-xs font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fg }}>
                                {project.testimonial.name}
                              </p>
                              <p className="text-xs" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                                {project.testimonial.role}
                              </p>
                            </div>
                          </div>
                          {project.disclosure && (
                            <p className="mt-3 text-xs leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted, opacity: 0.75 }}>
                              {project.disclosure}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right: stats */}
                    {project.stats && (
                    <div className="flex shrink-0 flex-col gap-4 md:w-52">
                      {project.stats.map(stat => (
                        <div key={stat.label} className="rounded-2xl p-5 text-center"
                          style={{ background: 'hsl(var(--background))', border: `1px solid ${border}` }}>
                          <div className="mb-1 break-words font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif', color: GRAD, fontSize: stat.value.length > 8 ? '1rem' : '1.875rem' }}>
                            {stat.value}
                          </div>
                          <div className="text-xs leading-snug" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                            {stat.label}
                          </div>
                        </div>
                      ))}
                    </div>
                    )}


                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden pb-24" style={{ background: 'hsl(var(--background))' }}>
        <HeroWave fadeBottom={58} bleed="bottom" />
        <div className="relative mx-auto max-w-3xl px-6">
          <motion.div className="text-center">
            <div className="mb-4 flex justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} fill="#FBBC04" style={{ color: '#FBBC04' }} />
              ))}
            </div>
            <h2 className="mb-3 text-2xl font-bold md:text-3xl" style={{ fontFamily: 'Outfit, sans-serif', color: 'hsl(var(--foreground))' }}>
              Want results like these?
            </h2>
            <p className="mx-auto mb-8 max-w-md text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'hsl(var(--muted-foreground))' }}>
              Every business on this page started with a conversation. No hard sell, no jargon — just an honest chat about where you are and where you want to be.
            </p>
            <Link to="/get-started"
              className="btn-glow inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-bold transition-all"
              style={{ background: `linear-gradient(135deg, ${GRAD} 0%, hsl(243 75% 45%) 100%)`, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Get Started <ArrowUpRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
